import React, { FCWithChildren, useCallback, useMemo } from 'react';
import { SSRConfig, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import { parseURLQueryParameters } from '@helpers/utils/catalog/catalog-url-helpers';
import ProductService from '@services/product-service';
import { Breadcrumb } from '@interfaces/models/breadcrumb';
import { Product } from '@interfaces/models/product';
import { Shipping } from '@interfaces/models/shipping';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import clsx from 'clsx';
import HeroPDP from '@components/hero-pdp/hero-pdp';
import CatalogPagesService from '@services/catalog-service';
import { CatalogPageMetadataResponse } from '@interfaces/api/responses/catalog-page-metadata-response';
import Breadcrumbs from '@components/molecules/products/breadcrumbs/breadcrumbs';
import useWindowSize from '@hooks/use-window-size';
import useOnReveal from '@hooks/use-on-reveal';
import Head, { generateAlternateLink } from '@components/atoms/head/head';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import useBreadcrumbsQuery from '@hooks/product/use-breadcrumbs-query';
import useHeroPdpSimilarProductsQuery, {
  getHeroPdpSimilarProductsQuery,
  HeroPdpSimilarProductQuery,
} from '@hooks/hero-pdp/use-hero-pdp-similar-products-query';
import useCatalogPageMetadataQuery from '@hooks/catalog-context/use-catalog-page-metadata-query';
import {
  BreadcrumbsQueryKeys,
  CatalogPageQuerykeys,
  GlobalQueryKeys,
  HeroPdpPageQueryKeys,
  ProductPageQueryKeys,
  ProductPdpPageQueryKeys,
} from '@enums/react-query-keys';
import ProductFeedLayout from '@components/generic-product-feed/product-feed-layout/product-feed-layout';
import ProductFeed from '@components/generic-product-feed/product-feed/product-feed';
import { useBucket } from '@context/bucket.context';
import useProductQuery from '@hooks/product/use-product-query';
import useProductShippingQuery from '@hooks/product/use-product-shipping-query';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import DefaultLayout from '@layouts/default-layout';
import logger from '@helpers/utils/logger/client';
import { getBreadcrumbs } from '@api/breadcrumbs';
import RequestError from '@errors/request-error';
import { hasSixMonthsExpired } from '@helpers/utils/date-utils';
import { WelcomeVoucher } from '@interfaces/models/welcomeVoucher';
import { getWelcomeVoucher } from '@api/contents';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import useProductFeedImpressionTracking, {
  getRowCount,
} from '@hooks/product-feed/use-product-feed-impression-tracking';
import { ContentsLocalizedPath } from '@interfaces/models/contentsLocalizedPath';
import { getAppInitialProps } from '@helpers/initial-props';
import styles from './index.module.scss';

export type HeroPDPProps = {
  dehydratedState: DehydratedState;
  pathnameWithoutPagination: string;
};

const ImpressionTrackingWrapper = ({ children, onRowReveal, index, productsOnRow }) => {
  const { containerRef } = useOnReveal(() => onRowReveal(index, productsOnRow), 1, false);
  return (
    <div
      className="impressionTrackingContainer"
      ref={containerRef}
    >
      {children}
    </div>
  );
};

const HeroPDPPage: NextPageWithLayout<HeroPDPProps> = (props): React.JSX.Element => {
  const { dehydratedState, pathnameWithoutPagination: baseUrl = '' } = props;
  const { t } = useTranslation();
  useHydrate(dehydratedState);

  const { breadcrumbs } = useBreadcrumbsQuery();
  const { catalogMetadata } = useCatalogPageMetadataQuery();
  const { product } = useProductQuery();
  const similarProductsQuery = useHeroPdpSimilarProductsQuery();
  const similarProducts = useMemo(() => similarProductsQuery.data?.similarProducts ?? [], [similarProductsQuery.data]);
  const productFeedCategory = similarProductsQuery.data?.productFeedCategory ?? '';
  const showMoreVisible = similarProductsQuery.data?.showMoreVisible ?? false;

  const { productShipping } = useProductShippingQuery();
  const { isWiderThanMd, isWiderThanLg, isWiderThanXl } = useWindowSize();
  const { sendAnalyticEvent } = useAnalyticEvents('hero_pdp');
  const { onRowReveal, rowCount, hasWindowSizeBeenSet } = useProductFeedImpressionTracking(productFeedCategory);

  const { isFeatureEnabled } = useBucket();
  const isHeroPdpUspEnabled = isFeatureEnabled((f) => !!f.isHeroPdpUspEnabled, true);
  const uspBlockInfo = useMemo(() => {
    return {
      showUspBlock: isHeroPdpUspEnabled,
      uspBlockProductCount: getRowCount({ isWiderThanXl, isWiderThanLg, isWiderThanMd }),
    };
  }, [isHeroPdpUspEnabled, isWiderThanMd, isWiderThanLg, isWiderThanXl]);

  const handleExploreMoreCta = () => {
    const splittedUrl = baseUrl.split('/');
    const urlWithLastSlugRemoved = splittedUrl.slice(0, splittedUrl.length - 2).join('/');
    window.location.href = urlWithLastSlugRemoved;
  };

  const heroPDPEmptyState = useMemo(() => {
    return {
      titleText: t('HERO_PDP.EMPTY_STATE.TITLE'),
      subTitleText: t('HERO_PDP.EMPTY_STATE.SUBTITLE'),
      clickHandler: handleExploreMoreCta,
      ctaText: t('HERO_PDP.EMPTY_STATE.EXPLORE_MORE_CTA', {
        subCategory: baseUrl.split('/')?.[baseUrl.split('/').length - 3],
      }),
    };
  }, []);

  const handleShowMore = () => {
    sendAnalyticEvent('hero_pdp_recirculation');
    // send personalized=0 so that mysizes are not considered when navigating to PLP pages
    window.location.href = `${baseUrl}?personalized=0`;
  };

  const ProductCardWrapper: FCWithChildren<{ index: number }> = useCallback(
    ({ children, index }) =>
      hasWindowSizeBeenSet && index % rowCount === 0 ? (
        <ImpressionTrackingWrapper
          productsOnRow={similarProducts.slice(index, index + rowCount)}
          index={index}
          onRowReveal={onRowReveal}
        >
          {children}
        </ImpressionTrackingWrapper>
      ) : (
        <>{children}</>
      ),
    [onRowReveal, similarProducts, rowCount, hasWindowSizeBeenSet],
  );

  return (
    <>
      <Head
        metadata={catalogMetadata?.metadata}
        alternateVersions={product?.alternateVersions ?? []}
        canonicalPath={product?.path}
      />
      <div className={clsx(styles.heroPDPPage__container, 'innerContainer')}>
        <div className={styles.heroPDPPage__header}>
          <div className={clsx(styles.heroPDPPage__header__breadcrumbs, 'd-none d-md-block')}>
            {breadcrumbs && (
              <Breadcrumbs
                breadcrumbs={breadcrumbs}
                isCatalogPage={false}
                customClass={styles['heroPDPPage__header__breadcrumbs--customClass']}
              />
            )}
          </div>
          {!!product && !!productShipping && <HeroPDP />}
        </div>
      </div>
      <ProductFeedLayout
        productList={
          <ProductFeed
            products={similarProducts}
            viewMoreVisible={showMoreVisible}
            similarProductTitle={'HERO_PDP.SIMILAR_PRODUCT.TITLE'}
            viewMoreCTAText="HERO_PDP.SIMILAR_PRODUCT.DISCOVER_MORE"
            handleViewMoreClick={handleShowMore}
            uspBlockInfo={uspBlockInfo}
            pageType="hero-pdp"
            emptyState={heroPDPEmptyState}
            ProductCardWrapper={ProductCardWrapper}
            shouldLazyLoadProducts={true}
            schemaOptions={{
              includeSchemaInProductCard: false,
            }}
            productCardProps={{ productFeedCategory }}
          />
        }
      />
    </>
  );
};

HeroPDPPage.Layout = [DefaultLayout];
HeroPDPPage.PageType = 'HeroPDPPage';

// if there is a 404 error for product or product is sold more than 6 months, redirect to PLP pages
const redirectToPlp = (product: Product, errorReason: string) => {
  const hasSoldDateExpired = product?.sold && hasSixMonthsExpired(product?.soldDate);
  return (!product && isRequest404Error(new RequestError(errorReason))) || hasSoldDateExpired;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req } = context;
  const { locale, serverAxiosRequestData, country, currency, regionName, language } = extractFromContext(context);
  const { pathnameWithoutPagination, productId } = parseURLQueryParameters(req.url);
  const { serverAnonymousId } = getOrCreateLDCookiesSsr(context);

  try {
    const queryClient = new QueryClient();
    const initialProps = await getAppInitialProps(context, queryClient);

    const apiRequests = [
      serverSideTranslations(locale, ['default-layout', 'common']),
      queryClient.fetchQuery({
        queryKey: [ProductPdpPageQueryKeys.GET_PDP_PRODUCT],
        queryFn: () => ProductService.getProduct(productId, serverAxiosRequestData),
      }),
      queryClient.fetchQuery({
        queryKey: [HeroPdpPageQueryKeys.GET_SIMILAR_PRODUCTS],
        queryFn: () =>
          getHeroPdpSimilarProductsQuery({
            locale: { country, currency, regionName, language },
            productId,
            url: req.url,
            serverAxiosRequestData,
            limit: 40,
          }),
      }),
      queryClient.fetchQuery({
        queryKey: [ProductPageQueryKeys.PRODUCT_SHIPPING],
        queryFn: () => ProductService.getProductShippingInformation(productId, serverAxiosRequestData),
      }),
      queryClient.fetchQuery({
        queryKey: [BreadcrumbsQueryKeys.BREADCRUMBS],
        queryFn: () => getBreadcrumbs(pathnameWithoutPagination, serverAxiosRequestData),
      }),
      queryClient.fetchQuery({
        queryKey: [CatalogPageQuerykeys.GET_CATALOG_METADATA],
        queryFn: () => CatalogPagesService.getCatalogPageMetadata(pathnameWithoutPagination, serverAxiosRequestData),
      }),
      queryClient.fetchQuery<WelcomeVoucher>({
        queryKey: [GlobalQueryKeys.WELCOME_VOUCHER, 'HERO_PDP', productId],
        queryFn: () => getWelcomeVoucher('HERO_PDP', serverAnonymousId, serverAxiosRequestData, productId),
      }),
    ];

    const promiseResults = await Promise.allSettled([...apiRequests]);

    let errorReason: string;
    const [translations, product, , , , metadata] = promiseResults.map((promiseResult) => {
      if (promiseResult.status === 'fulfilled') {
        return promiseResult.value;
      }
      errorReason = promiseResult.reason;
      return null;
    }) as [
      SSRConfig,
      Product | null,
      Shipping | null,
      HeroPdpSimilarProductQuery,
      Breadcrumb[],
      CatalogPageMetadataResponse,
    ];

    // We redirect to correct localized path, e.g. if user with US cookies tries to access french url, we redirect to US localized one
    if (metadata) {
      const { path } = metadata;

      if (redirectToPlp(product, errorReason)) {
        return {
          redirect: {
            destination: path,
            permanent: true,
          },
        };
      }

      if (path !== pathnameWithoutPagination) {
        const { resolvedUrl } = context;
        const [, queryParams] = resolvedUrl.split('?');
        const hasQueryParams: boolean = !!queryParams;

        return {
          redirect: {
            destination: `${path}${hasQueryParams ? `?${queryParams}` : ''}`,
            permanent: false,
          },
        };
      }
    }

    if (product?.alternateVersions) {
      // map the alternate versions to be absolute urls
      product.alternateVersions = product.alternateVersions.map((version: ContentsLocalizedPath) => {
        return {
          ...version,
          path: generateAlternateLink(version),
        };
      });
    }

    return {
      props: {
        ...initialProps,
        ...translations,
        dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))), // fix for https://github.com/TanStack/query/issues/1458
        pathnameWithoutPagination,
      },
    };
  } catch (error) {
    // Axios 404 errors should get redirected straight to error page
    if (isRequestError(error)) {
      if (!isRequest404Error(error)) {
        logger.error(
          error,
          'There was an api request error on the Hero PDP Page - getServerSideProps - [[...heroPDPSlug]].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    logger.error(
      error,
      'The server failed to render the Hero PDP Page - getServerSideProps - [[...heroPDPSlug]].page.tsx',
    );
    // Other errors will get logged
    throw error;
  }
};

export default HeroPDPPage;
